import { AppHTTPService } from '@/api/http-service'
import { Icon, cn } from '@flatfile/baseline'
import { OAuth } from '@/auth/oauth'
import { useQuery } from '@tanstack/react-query'
import { formatDistanceToNow } from 'date-fns'
import { useState } from 'react'
import { Link, useNavigate, useParams } from 'react-router-dom'
import obviousSvg from '/obvious.svg'
import { useAuth } from '../../auth/AuthProvider'
import { performLogout } from '../../auth/logout'

export function Sidebar() {
  const [isHovered, setIsHovered] = useState(false)
  const [accountHovered, setAccountHovered] = useState(false)
  const navigate = useNavigate()
  const { projectId } = useParams()
  const { isGuest, isAuthenticated, isLoading } = useAuth()

  // Fetch recent projects
  const { data: projects } = useQuery({
    queryKey: ['projects'],
    queryFn: async () => {
      const response = await AppHTTPService.client.projects.list()
      return response as Array<{
        id: string
        name: string
        updatedAt?: string
      }>
    },
    enabled: isAuthenticated && !isLoading, // Only run when authenticated and not loading
  })

  // Get the 5 most recent projects
  const recentProjects = (projects || [])
    .sort((a, b) => {
      const dateA = a.updatedAt ? new Date(a.updatedAt).getTime() : 0
      const dateB = b.updatedAt ? new Date(b.updatedAt).getTime() : 0
      return dateB - dateA
    })
    .slice(0, 5)

  const handleCreateProject = async () => {
    const newProject =
      await AppHTTPService.client.projects.create('New Project')
    if (newProject?.id) {
      navigate(`/projects/${newProject.id}/prepare-chat`)
    }
  }

  const handleSignOut = async () => {
    try {
      await performLogout(isGuest)
      window.location.href = '/'
    } catch (error) {
      console.error('Sign out error:', error)
      // Fallback redirect even if logout fails
      window.location.href = '/'
    }
  }

  return (
    <div
      className='fixed left-0 top-0 h-full bg-white border-r border-gray-200 text-gray-900 transition-all duration-300 ease-in-out z-50'
      style={{ width: isHovered ? '16rem' : '4rem' }}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
    >
      {/* Inner container */}
      <div className='h-full flex flex-col overflow-hidden'>
        {/* Logo/Brand */}
        <div className='border-b border-gray-200 p-4'>
          <Link
            to='/'
            className='flex items-center gap-3 no-underline relative'
            style={{ overflow: 'hidden', minWidth: '32px' }}
          >
            <div className='w-8 h-8 rounded-lg border border-gray-300 flex items-center justify-center flex-shrink-0 hover:bg-gray-100 transition-colors overflow-hidden relative z-10 bg-white'>
              <img src={obviousSvg} alt='Obvious' className='w-6 h-6' />
            </div>
            <span
              className={cn(
                'font-semibold text-lg whitespace-nowrap text-gray-900 transition-all duration-300 ease-in-out relative z-0',
                isHovered
                  ? 'translate-x-0 opacity-100'
                  : '-translate-x-full opacity-0'
              )}
              style={{ width: '6rem' }}
            >
              Obvious
            </span>
          </Link>
        </div>

        {/* Current Project Info */}
        {projectId && (
          <div className='px-4 py-3 border-b border-gray-200 overflow-hidden'>
            <div
              className={cn(
                'transition-all duration-300 ease-in-out',
                isHovered
                  ? 'translate-x-0 opacity-100'
                  : '-translate-x-full opacity-0'
              )}
            >
              <div className='text-xs text-gray-500 uppercase tracking-wider mb-1'>
                Current Project
              </div>
              <div className='text-sm font-medium truncate'>
                {projects?.find((p) => p.id === projectId)?.name ||
                  'Loading...'}
              </div>
            </div>
          </div>
        )}

        {/* Navigation */}
        <nav className='space-y-2 p-4'>
          {/* Projects Grid */}
          <Link to='/projects'>
            <button
              className={cn(
                'w-full flex items-center rounded-lg transition-colors relative group',
                'hover:bg-gray-100',
                window.location.pathname === '/projects' && 'bg-gray-100',
                isHovered ? 'gap-3 px-3 py-2' : 'justify-center py-2'
              )}
            >
              <div className='w-5 h-5 rounded border border-gray-300 flex items-center justify-center text-sm font-semibold flex-shrink-0'>
                ≡
              </div>
              {isHovered && (
                <span className='transition-all duration-300 ease-in-out whitespace-nowrap'>
                  All Projects
                </span>
              )}
              {!isHovered && (
                <div className='absolute left-full ml-2 px-2 py-1 bg-gray-900 text-white text-sm rounded opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none whitespace-nowrap z-10'>
                  All Projects
                </div>
              )}
            </button>
          </Link>

          {/* Create New Project */}
          <button
            onClick={handleCreateProject}
            className={cn(
              'w-full flex items-center rounded-lg transition-colors relative group',
              'hover:bg-gray-100',
              isHovered ? 'gap-3 px-3 py-2' : 'justify-center py-2'
            )}
          >
            <Icon name='plus-small' size={20} className='flex-shrink-0' />
            {isHovered && (
              <span className='transition-all duration-300 ease-in-out whitespace-nowrap'>
                New Project
              </span>
            )}
            {!isHovered && (
              <div className='absolute left-full ml-2 px-2 py-1 bg-gray-900 text-white text-sm rounded opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none whitespace-nowrap z-10'>
                New Project
              </div>
            )}
          </button>
        </nav>

        {/* Recent Projects */}
        {recentProjects && recentProjects.length > 0 && (
          <div className='px-4 pb-4 flex-1 overflow-y-auto overflow-x-hidden'>
            <div
              className={cn(
                'transition-all duration-300 ease-in-out',
                isHovered
                  ? 'translate-x-0 opacity-100'
                  : '-translate-x-full opacity-0'
              )}
            >
              <h3 className='text-xs font-semibold text-gray-500 uppercase tracking-wider mb-2'>
                Recent Projects
              </h3>
              <div className='space-y-1'>
                {recentProjects.map((project) => (
                  <Link
                    key={project.id}
                    to={`/projects/${project.id}/prepare-chat`}
                  >
                    <button
                      className={cn(
                        'w-full text-left px-3 py-2 rounded-lg transition-colors text-sm',
                        'hover:bg-gray-100',
                        projectId === project.id && 'bg-gray-100'
                      )}
                    >
                      <div className='truncate font-medium'>
                        {project.name || 'Untitled Project'}
                      </div>
                      {project.updatedAt && (
                        <div className='text-xs text-gray-500 mt-0.5'>
                          {formatDistanceToNow(new Date(project.updatedAt), {
                            addSuffix: true,
                          })}
                        </div>
                      )}
                    </button>
                  </Link>
                ))}
              </div>
            </div>
          </div>
        )}

        {/* User Menu at Bottom */}
        <div className='mt-auto border-t border-gray-200 p-4 space-y-2 overflow-hidden'>
          {/* Account Button with Hover Logout */}
          <div
            className='relative'
            onMouseEnter={() => setAccountHovered(true)}
            onMouseLeave={() => setAccountHovered(false)}
          >
            <Link to='/account'>
              <button
                className={cn(
                  'w-full flex items-center rounded-lg transition-colors hover:bg-gray-100 relative group',
                  isHovered ? 'gap-3 px-3 py-2' : 'justify-center py-2'
                )}
              >
                <Icon name='people' size={20} className='flex-shrink-0' />
                {isHovered && (
                  <span className='transition-all duration-300 ease-in-out whitespace-nowrap'>
                    Account
                  </span>
                )}
                {!isHovered && (
                  <div className='absolute left-full ml-2 px-2 py-1 bg-gray-900 text-white text-sm rounded opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none whitespace-nowrap z-10'>
                    Account
                  </div>
                )}
              </button>
            </Link>

            {/* Logout Button on Hover */}
            {accountHovered && isHovered && (
              <button
                onClick={handleSignOut}
                className='w-full flex items-center gap-3 px-3 py-2 mt-1 rounded-lg transition-colors hover:bg-red-50 hover:text-red-700 text-gray-600'
              >
                <Icon name='logout' size={20} className='flex-shrink-0' />
                <span className='transition-all duration-300 ease-in-out whitespace-nowrap'>
                  {isGuest ? 'Clear Session' : 'Sign Out'}
                </span>
              </button>
            )}
          </div>
        </div>
      </div>

      {/* Hover Indicator */}
      <div
        className={cn(
          'absolute right-0 top-1/2 -translate-y-1/2 w-1 h-16 bg-gray-300 rounded-l-full transition-opacity duration-300',
          isHovered ? 'opacity-0' : 'opacity-100'
        )}
      />
    </div>
  )
}
